import React, { useEffect, useRef, useState, type RefObject } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HandIcon } from 'lucide-react';
const EASE = [0.23, 1, 0.32, 1] as const;
/** Distance from the device's bottom edge to the Snak footer controls (▶ · counter · NEXT →). */
const FOOTER_OFFSET = 45;
type Cue = {
  title: string;
  body: string;
};
type PageCue = Cue & {
  /** Finds the element on this chapter where the interaction lives. */
  find: (page: Element) => Element | null;
};
const byText = (root: Element, selector: string, re: RegExp) => Array.from(root.querySelectorAll(selector)).find(el => re.test(el.textContent ?? '')) ?? null;

/** One cue per chapter, pointing at that chapter's interaction. */
const PAGE_CUES: PageCue[] = [{
  title: 'Open the study design',
  body: 'See how MINT was run, and its limitations.',
  find: p => byText(p, 'button', /explore study design/i)
}, {
  title: 'Tap a result',
  body: 'Open the definition, context and limitations.',
  find: p => p.querySelector('[role="button"]')
}, {
  title: 'Switch Skin / Blood',
  body: 'See how each compartment is involved.',
  find: p => p.querySelector('[role="tablist"]')
}, {
  title: 'Compare B0, B1, B2',
  body: 'Watch the blood film change with each class.',
  find: p => p.querySelector('[role="tablist"]')
}, {
  title: 'Choose a compartment',
  body: 'Each response rate counts up as you select it.',
  find: p => p.querySelector('[role="tablist"]')
}, {
  title: 'Step through the mechanism',
  body: 'Tap 1–3, or switch to Clinical evidence.',
  find: p => p.querySelector('ol')
}, {
  title: 'Explore further',
  body: 'Open patient selection, outcomes or management.',
  find: p => p.querySelector('ul button')
}, {
  title: 'Go deeper',
  body: 'Open the full results, evidence and references.',
  find: p => byText(p, 'button', /explore mint results/i)
}];
const TRY_CUE: Cue = {
  title: 'Start here',
  body: 'Tap NEXT → to move through the chapters at your own pace — or ▶ for the guided story.'
};
const NEXT_CUE: Cue = {
  title: 'You’re in the Snak',
  body: 'Tap NEXT → for the next chapter, or ▶ for the guided story.'
};
const CONTINUE_CUE: Cue = {
  title: 'Continue with NEXT →',
  body: 'Move on to the next chapter. Use ← to go back.'
};
const SHEET_CUE: Cue = {
  title: 'Reading the evidence',
  body: 'Close the panel to return to the chapter.'
};
type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
};
type Placement = {
  y: number;
  key: string;
  cue: Cue;
  ring?: Rect;
};
const LAST_INDEX = PAGE_CUES.length - 1;
function currentIndex(host: Element) {
  const counter = Array.from(host.querySelectorAll('button[aria-label="Open chapter menu"]')).find(b => /^\d{2}/.test(b.textContent?.trim() ?? ''));
  return counter ? parseInt(counter.textContent!.trim().slice(0, 2), 10) - 1 : -1;
}

/**
 * Side note attached to the device by a hairline. Before the first touch it points at the footer
 * controls; afterwards it follows each chapter's interaction up and down the screen.
 * It reads the Snak's rendered screen only — the Snak itself is not modified.
 */
export function PhoneCue({
  engaged,
  hostRef
}: {
  engaged: boolean;
  hostRef: RefObject<HTMLElement>;
}) {
  const [place, setPlace] = useState<Placement | null>(null);
  // Chapters whose interaction the visitor has already tried — the cue then hands over to NEXT →.
  const doneRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as Element | null;
      if (!el?.closest('.snak-page')) return;
      if (!el.closest('button, [role="button"], [role="tab"]')) return;
      const index = currentIndex(host);
      if (index >= 0) doneRef.current.add(index);
    };
    host.addEventListener('pointerdown', onPointerDown, true);
    const measure = () => {
      const hostRect = host.getBoundingClientRect();
      // Level with the Snak's NEXT button when it can be found; otherwise a fixed footer offset.
      const nextBtn = byText(host, 'footer button', /^\s*(next|chapters)/i);
      const nextRect = nextBtn?.getBoundingClientRect();
      const footerY = nextRect ? nextRect.top + nextRect.height / 2 - hostRect.top : hostRect.height - FOOTER_OFFSET;
      const ring: Rect | undefined = nextRect ? {
        x: nextRect.left - hostRect.left,
        y: nextRect.top - hostRect.top,
        w: nextRect.width,
        h: nextRect.height
      } : undefined;
      // Before the first tap, point at NEXT: that's where the visitor starts.
      let next: Placement = {
        y: footerY,
        key: 'try',
        cue: TRY_CUE,
        ring
      };
      if (engaged) {
        next = {
          y: footerY,
          key: 'next',
          cue: NEXT_CUE
        };
        const sheet = host.querySelector('[role="dialog"]');
        const page = host.querySelector('.snak-page');
        const index = currentIndex(host);
        if (sheet && index >= 0) doneRef.current.add(index);
        if (sheet) {
          next = {
            y: hostRect.height * 0.45,
            key: 'sheet',
            cue: SHEET_CUE
          };
        } else if (doneRef.current.has(index) && index < LAST_INDEX) {
          // Interaction tried: make the way forward obvious.
          next = {
            y: footerY,
            key: `continue${index}`,
            cue: CONTINUE_CUE,
            ring
          };
        } else if (page && PAGE_CUES[index]) {
          const target = PAGE_CUES[index].find(page);
          const pageRect = page.getBoundingClientRect();
          if (target) {
            const r = target.getBoundingClientRect();
            const center = r.top + Math.min(r.height, 80) / 2;
            // Only follow the target while it's actually visible in the phone screen.
            if (center > pageRect.top + 12 && center < pageRect.bottom - 12) {
              next = {
                y: center - hostRect.top,
                key: `p${index}`,
                cue: PAGE_CUES[index]
              };
            }
          }
        }
      }
      setPlace(prev => prev && prev.key === next.key && Math.abs(prev.y - next.y) < 2 && !!prev.ring === !!next.ring ? prev : next);
    };
    measure();
    const id = window.setInterval(measure, 200);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', measure);
      host.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [engaged, hostRef]);
  if (!place) return null;
  return <>
      <AnimatePresence data-id="element-129" data-mp="2sbvam10brspl">
        {place.ring && <motion.span key="next-ring" className="next-ring pointer-events-none absolute z-20 rounded-full border-2 border-teal" style={{
        left: place.ring.x - 8,
        top: place.ring.y - 4,
        width: place.ring.w + 16,
        height: place.ring.h + 8
      }} initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} aria-hidden="true" data-id="element-130" data-mp="1unljih22upd9" />}
      </AnimatePresence>
    <motion.div className="pointer-events-none absolute left-full top-0 z-10 hidden w-[200px] items-start lg:flex" aria-hidden="true" initial={false} animate={{
      y: place.y - 11
    }} transition={{
      duration: 0.28,
      ease: EASE
    }} data-id="element-131" data-mp="3k3b5eic1clqw">
      <span className="mt-[11px] h-px w-8 shrink-0 bg-teal/60" data-id="element-132" data-mp="zkjbqlfnxkci" />
      <span className="relative mt-[8px] h-[7px] w-[7px] shrink-0 rounded-full bg-teal" data-id="element-133" data-mp="umq3znypoia4">
        <span className="cue-ping absolute inset-0 rounded-full bg-teal" data-id="element-134" data-mp="pndl1eaynvct" />
      </span>
      <div className="relative pl-3" data-id="element-135" data-mp="pl0sk77ku2li">
        <AnimatePresence mode="wait" initial={false} data-id="element-136" data-mp="ko0x0q8csi8n">
          <motion.div key={place.key} initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: EASE
          }} data-id="element-137" data-mp="kqensmw038ae">
            <p className="text-[14px] font-semibold text-ink" data-id="element-138" data-mp="2b19kab7k6o9a">{place.cue.title}</p>
            <p className="mt-1 text-[12.5px] leading-snug text-graphite" data-id="element-139" data-mp="261wmwwj29ysg">{place.cue.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
    </>;
}

/** Compact version for screens where the side note has no room. */
export function PhoneCueInline() {
  return <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-teal lg:hidden" data-id="element-140" data-mp="156hm2n38n7uf">
      <HandIcon size={12} strokeWidth={2} aria-hidden="true" data-id="element-141" data-mp="1a513qbastxgz" />
      INTERACTIVE · TAP NEXT → OR ▶
    </span>;
}